import React, { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react';

const BrandCollectionTree = ({ brands, collections, selectedBrandId, selectedCollectionId, onSelectBrand, onSelectCollection, brandCounts, collectionCounts }) => {
  const [expandedBrands, setExpandedBrands] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Группируем коллекции по брендам
  const brandCollectionsMap = useMemo(() => {
    const map = {};
    if (!collections || !Array.isArray(collections)) return map;
    
    collections.forEach(col => {
      if (!map[col.brand_id]) {
        map[col.brand_id] = [];
      }
      map[col.brand_id].push(col);
    });
    
    return map;
  }, [collections]);

  // Фильтруем бренды по поисковому запросу
  const filteredBrands = useMemo(() => {
    if (!brands || !Array.isArray(brands)) return [];

    // Если есть brandCounts — показываем только бренды с ненулевым счётчиком
    // Иначе fallback на product_count из самого бренда
    let result = brands.filter(brand => {
      const count = brandCounts ? (brandCounts[brand.id] || 0) : (brand.product_count || 0);
      return count > 0;
    });

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(brand => {
        if (brand.name.toLowerCase().includes(query)) return true;
        const brandCols = brandCollectionsMap[brand.id] || [];
        return brandCols.some(col => col.name.toLowerCase().includes(query));
      });
    }

    return result.slice().sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [brands, searchQuery, brandCollectionsMap, brandCounts]);

  const toggleBrand = (brandId) => {
    const newExpanded = new Set(expandedBrands);
    if (newExpanded.has(brandId)) {
      newExpanded.delete(brandId);
    } else {
      newExpanded.add(brandId);
    }
    setExpandedBrands(newExpanded);
  };

  const handleBrandClick = (brandId) => {
    onSelectBrand(brandId);
    onSelectCollection(null);
  };

  const handleCollectionClick = (collectionId, brandId) => {
    // Разворачиваем бренд при выборе коллекции
    const newExpanded = new Set(expandedBrands);
    newExpanded.add(brandId);
    setExpandedBrands(newExpanded);
    
    onSelectBrand(brandId);
    onSelectCollection(collectionId);
  };

  return (
    <div style={{
      width: '320px',
      height: '100vh',
      overflowY: 'auto',
      borderRight: '1px solid #e5e7eb',
      padding: '16px',
      backgroundColor: 'white',
      flexShrink: 0
    }}>
      <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
        Бренды и коллекции
      </h3>
      
      {/* Поиск */}
      <input
        type="text"
        className="input"
        placeholder="Поиск..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: '12px', fontSize: '13px' }}
      />

      {/* Кнопка "Все товары" */}
      <div
        onClick={() => {
          onSelectBrand(null);
          onSelectCollection(null);
        }}
        style={{
          padding: '8px 12px',
          marginBottom: '8px',
          cursor: 'pointer',
          borderRadius: '6px',
          backgroundColor: !selectedBrandId && !selectedCollectionId ? '#eff6ff' : 'transparent',
          color: !selectedBrandId && !selectedCollectionId ? '#2563eb' : '#374151',
          fontWeight: !selectedBrandId && !selectedCollectionId ? '600' : '400',
          fontSize: '14px'
        }}
        onMouseEnter={(e) => {
          if (!selectedBrandId && !selectedCollectionId) return;
          e.currentTarget.style.backgroundColor = '#f3f4f6';
        }}
        onMouseLeave={(e) => {
          if (!selectedBrandId && !selectedCollectionId) return;
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        Все товары
      </div>

      {/* Дерево брендов и коллекций */}
      <div>
        {filteredBrands.map(brand => {
          const isExpanded = expandedBrands.has(brand.id);
          const brandCollections = brandCollectionsMap[brand.id] || [];
          const isBrandSelected = selectedBrandId === brand.id && !selectedCollectionId;
          
          // Фильтруем коллекции по поисковому запросу и показываем только с товарами
          const visibleCollections = brandCollections
            .filter(col => {
              const count = collectionCounts ? (collectionCounts[col.id] || 0) : (col.product_count || 0);
              return count > 0;
            })
            .filter(col => !searchQuery || col.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

          return (
            <div key={brand.id} style={{ marginBottom: '4px' }}>
              {/* Бренд */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  backgroundColor: isBrandSelected ? '#eff6ff' : 'transparent',
                  color: isBrandSelected ? '#2563eb' : '#374151',
                  fontWeight: isBrandSelected ? '600' : '400'
                }}
                onMouseEnter={(e) => {
                  if (isBrandSelected) return;
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  if (isBrandSelected) return;
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  onClick={() => toggleBrand(brand.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '6px',
                    padding: '2px'
                  }}
                >
                  {visibleCollections.length > 0 ? (
                    isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  ) : (
                    <div style={{ width: '16px' }} />
                  )}
                </div>
                
                <div
                  onClick={() => handleBrandClick(brand.id)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px'
                  }}
                >
                  {isExpanded ? <FolderOpen size={16} /> : <Folder size={16} />}
                  <span>{brand.name}</span>
                  <span style={{ 
                    marginLeft: 'auto', 
                    fontSize: '12px', 
                    color: '#9ca3af',
                    fontWeight: '400'
                  }}>
                    {brandCounts ? (brandCounts[brand.id] || 0) : (brand.product_count || 0)}
                  </span>
                </div>
              </div>

              {/* Коллекции */}
              {isExpanded && visibleCollections.length > 0 && (
                <div style={{ marginLeft: '24px' }}>
                  {visibleCollections.map(collection => {
                    const isCollectionSelected = selectedCollectionId === collection.id;
                    
                    return (
                      <div
                        key={collection.id}
                        onClick={() => handleCollectionClick(collection.id, brand.id)}
                        style={{
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderRadius: '6px',
                          backgroundColor: isCollectionSelected ? '#eff6ff' : 'transparent',
                          color: isCollectionSelected ? '#2563eb' : '#6b7280',
                          fontSize: '13px',
                          fontWeight: isCollectionSelected ? '600' : '400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                        onMouseEnter={(e) => {
                          if (isCollectionSelected) return;
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          if (isCollectionSelected) return;
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span>{collection.name}</span>
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {collectionCounts ? (collectionCounts[collection.id] || 0) : (collection.product_count || 0)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(BrandCollectionTree);
